// Модель таблицы «Сделка по факту»: строки и суммы БЕЗ НДС (конвенция платформы — НДС только в
// документах). Снятие НДС — тем же способом, что и dealPrice.ts (totalAmount − totalNds). Чистые
// функции (без React) — единый источник для DealFactTable.
import { isoToRu } from '@/lib/date';
import type { BargainStepsInfo } from '@/types/deals';

export interface FactRow {
  readonly key: string;
  /** День (зачёт по дню) или период «дата – дата» (зачёт по среднему). */
  readonly period: string;
  readonly tons: number | undefined;
  readonly quality: number | undefined;
  /** Цена за тонну, БЕЗ НДС. */
  readonly price: number | undefined;
  /** Сумма строки, БЕЗ НДС. */
  readonly amount: number | undefined;
}

export interface FactTable {
  /** true → зачёт по дню (строка на день); false → по среднему (одна строка-период). */
  readonly byDay: boolean;
  readonly rows: FactRow[];
  /** Итог без НДС (totalAmount − totalNds), авторитетный — совпадает с «Окончательным расчётом». */
  readonly totalNet: number | undefined;
  readonly totalWeight: number | undefined;
}

const ruDate = (iso: string | undefined): string => isoToRu(iso?.slice(0, 10)) ?? '—';

// Коэффициент снятия НДС из сумм finalReferenceInfo (бэк отдаёт с НДС, если продавец — плательщик).
// Единый с dealPrice.ts способ: totalAmount − totalNds (для равномерной ставки factor = 1/(1+ставка),
// т.е. построчная цена gross/(1+ставка) точна). Без НДС / нет данных → factor = 1.
function netFactor(totalAmount: number | undefined, totalNds: number | undefined): number {
  const gross = totalAmount ?? 0;
  return gross > 0 ? (gross - (totalNds ?? 0)) / gross : 1;
}

// Зачёт качества определяем по ФАКТИЧЕСКИ присутствующим данным, а не по qualityCreditType: бэк
// присылает ровно одно из полей (unloadedDays | unloadedPeriod), и data-driven устойчив к рассинхрону
// флага с payload. Цены/суммы переводим в БЕЗ НДС. Нет данных → rows = [].
export function buildFactTable(info: BargainStepsInfo): FactTable {
  const fr = info.finalReferenceInfo;
  if (!fr) {
    return { byDay: false, rows: [], totalNet: undefined, totalWeight: undefined };
  }
  const factor = netFactor(fr.totalAmount, fr.totalNds);
  const net = (v: number | undefined): number | undefined => (v == null ? undefined : v * factor);
  const totalNet = fr.totalAmount == null ? undefined : fr.totalAmount - (fr.totalNds ?? 0);

  const days = fr.unloadedDays ?? [];
  if (days.length > 0) {
    const rows = days.map((d, i) => ({
      key: d.date ?? String(i),
      period: ruDate(d.date),
      tons: d.weight,
      quality: d.mainPropertyValue,
      price: net(d.price),
      amount: net(d.amount),
    }));
    return { byDay: true, rows, totalNet, totalWeight: fr.totalWeight };
  }

  const p = fr.unloadedPeriod;
  const rows = p
    ? [
        {
          key: 'period',
          period: `${ruDate(p.dateFrom)} – ${ruDate(p.dateTo)}`,
          tons: p.weight,
          quality: p.mainPropertyValue,
          price: net(p.price),
          amount: net(p.amount),
        },
      ]
    : [];
  return { byDay: false, rows, totalNet, totalWeight: fr.totalWeight };
}
